/**
 * Auth state management — JWT token and user stored in localStorage.
 */

export interface Profile {
  ftp: number | null;
  weight_kg: number | null;
  height_cm: number | null;
  birth_year: number | null;
  // Heart-rate anchors for training load on rides without power (bpm).
  lthr?: number | null;
  max_hr?: number | null;
  level: string | null;
  goals: string[] | null;
  // Nutrition fields (Slice 2 backend). Optional on the API and in the
  // UI; nutrition guidance degrades gracefully when they are null.
  sex: string | null;
  age: number | null;
  primary_sport: string | null;
  weekly_training_hours: number | null;
  // Nutrition-side goal label, distinct from the cycling ``goals``
  // array. See ``api/utils/nutrition_prompt.GOAL_LABELS`` for the
  // backend-side mapping.
  goal: string | null;
  dietary_preferences: string[] | null;
  allergies: string[] | null;
  excluded_foods: string[] | null;
}

export interface PlanUsage {
  used: number;
  limit: number;
}

export interface User {
  id: number;
  email: string;
  name: string;
  avatar_url: string | null;
  profile: Profile | null;
  // Email verification (docs/EMAIL_VERIFICATION_PLAN.md). Optional: a user
  // saved by an older build has none until the next /auth/me.
  email_verified?: boolean;
  // How the account signs in: ``email``, ``google``, ``apple``.
  sign_in_methods?: string[];
  // Pro plan (API ``services/entitlements.plan_summary``). Optional: a user
  // saved by an older build has none until the next /auth/me.
  plan?: 'free' | 'pro';
  pro_source?: string | null;
  pro_until?: string | null;
  trial?: boolean;
  free_chat_left?: number | null;
  free_chat_monthly?: number;
  // New rides from files a free user may still add this month (null: Pro).
  free_uploads_left?: number | null;
  free_uploads_monthly?: number | null;
  usage_today?: { chat: PlanUsage; generation: PlanUsage } | null;
  // Feature switches (API ``services/entitlements.features_for``). Strava is
  // owner-only (docs/DATA_SOURCES_PLAN.md); Garmin Connect is behind
  // GARMIN_ENABLED / GARMIN_PUBLIC (docs/GARMIN_PLAN.md), intervals.icu behind
  // INTERVALS_ENABLED / INTERVALS_PUBLIC (docs/INTERVALS_PLAN.md), other
  // activities (runs, swims, gym) behind ACTIVITIES_ENABLED / ACTIVITIES_PUBLIC
  // (docs/ACTIVITIES_PLAN.md §9).
  features?: { strava?: boolean; garmin?: boolean; intervals?: boolean; activities?: boolean };
  // The rider's IANA time zone as last sent (``PUT /auth/timezone``); the
  // server's days follow it (docs/USER_TIMEZONE_PLAN.md). Null until sent.
  timezone?: string | null;
}

const TOKEN_KEY = 'cycling_jwt';
const USER_KEY = 'cycling_user';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string): void {
  localStorage.setItem(TOKEN_KEY, t);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function getUser(): User | null {
  const u = localStorage.getItem(USER_KEY);
  if (!u) return null;
  try {
    return JSON.parse(u) as User;
  } catch {
    // Corrupt value: runs during store creation, before the ErrorBoundary
    // mounts, so throwing here would white-screen the app.
    localStorage.removeItem(USER_KEY);
    return null;
  }
}

export function setUser(u: User): void {
  localStorage.setItem(USER_KEY, JSON.stringify(u));
}

export function clearAuth(): void {
  clearToken();
  localStorage.removeItem(USER_KEY);
}
